// Shared data loader + actions for the customer (contact) window - used by both
// the centered-window variant and the full-page variant. Loads the contact plus
// every per-contact aggregation in parallel, derives a merged activity timeline,
// and can open the real Order / Quote / Task detail windows on click.
import OrderModel from '../model/Order'
import OrderWindow from '../windows/OrderWindow'
import QuoteModel from '../model/Quote'
import QuoteWindow from '../windows/QuoteWindow'
import TaskModel from '../model/Task'
import TaskWindow from '../windows/TaskWindow'
import SurveyTaskModel from '../model/SurveyTask'
import SurveyTaskWindow from '../windows/SurveyTaskWindow'
import MoveTaskModel from '../model/MoveTask'
import MoveTaskWindow from '../windows/MoveTaskWindow'
import CourierTaskModel from '../model/CourierTask'
import CourierTaskWindow from '../windows/CourierTaskWindow'

const TASK_KINDS = {
	move: { model: MoveTaskModel, window: MoveTaskWindow },
	survey: { model: SurveyTaskModel, window: SurveyTaskWindow },
	pickup: { model: CourierTaskModel, window: CourierTaskWindow },
	delivery: { model: CourierTaskModel, window: CourierTaskWindow }
};

export default {
	data() {
		return {
			cw: {
				contact: null,
				stats: null,
				orders: [],
				quotes: [],
				tasks: [],
				packaging: { items: [], total_qty: 0, total_value: 0, frozen: false, overdue: false, last_notice: null, notice_count: 0 },
				emails: [],
				sms: [],
				loading: true
			}
		};
	},
	computed: {
		cwActivity() {
			const ev = [];
			const add = (type, ts, title, sub, id, taskType) => { if (ts) ev.push({ type: type, ts: new Date(ts), title: title, sub: sub, id: id || null, taskType: taskType || null }); };

			this.cw.orders.forEach(o => add('order', o.created, 'Ordre #' + o.num, o.description || '', o.id));
			this.cw.quotes.forEach(q => add('quote', q.created, 'Tilbud #' + q.num, '', q.id));
			this.cw.tasks.forEach(t => add('task', t.created || t.due_start_from, 'Opgave #' + t.num, t.name || '', t.id, t.type));
			this.cw.emails.forEach(e => add('email', e.created, e.subject || 'E-mail', e.email || ''));
			this.cw.sms.forEach(s => add('sms', s.created, s.content || 'SMS', s.phone || ''));

			return ev.sort((a, b) => b.ts - a.ts);
		}
	},
	methods: {
		async cwLoad(id) {
			const ax = this.axios;
			this.cw.loading = true;

			const get = (suffix, fallback) => ax.get('/contacts/' + id + suffix).then(r => r.data).catch(() => fallback);

			const [contact, stats, orders, quotes, tasks, pack, emails, sms] = await Promise.all([
				this.cw.contact ? Promise.resolve(this.cw.contact) : get('/', null),
				get('/stats/', null),
				get('/orders/', { _items: [] }),
				get('/quotes/', { _items: [] }),
				get('/tasks/', { _items: [] }),
				get('/pack/overview/', { items: [], total_qty: 0, total_value: 0, frozen: false, overdue: false, last_notice: null, notice_count: 0 }),
				get('/emails/', { _items: [] }),
				get('/sms/', { _items: [] })
			]);

			this.cw.contact = contact;
			this.cw.stats = stats;
			this.cw.orders = (orders && orders._items) || [];
			this.cw.quotes = (quotes && quotes._items) || [];
			this.cw.tasks = (tasks && tasks._items) || [];
			this.cw.packaging = pack || this.cw.packaging;
			this.cw.emails = (emails && emails._items) || [];
			this.cw.sms = (sms && sms._items) || [];
			this.cw.loading = false;
		},

		async cwOpen(p) {
			const ax = this.axios;
			const stack = this.$store.state.windowStack;
			let model, window;

			if (p.kind === 'order') { model = OrderModel; window = OrderWindow; }
			else if (p.kind === 'quote') { model = QuoteModel; window = QuoteWindow; }
			else { const k = TASK_KINDS[p.type] || { model: TaskModel, window: TaskWindow }; model = k.model; window = k.window; }

			const ent = await model.read(ax, p.id);
			if (!ent) return;

			stack.push(window, {
				value: ent,
				submit: async function (entity) {
					if (await model.save(ax, entity)) {
						stack.pop();
					}
				}
			});
		}
	}
};
